import { gql } from '@libs/trip-planner-interfaces';

export const NoteFragment = gql(`
  fragment Note on NoteResponseQL {
    severity
    text
    fromDate
    toDate
  }`);

export const MoneyFragment = gql(`
  fragment Money on MoneyQL {
    amount
    currency
    currencySymbol
  }`);

export const LegFragment = gql(`
    fragment Leg on LegQL {
    durationInMinutes
    luggageInformation {
      included
      pieces
      type
      unit
      weight
    }
    platingCarrier
    segments {
      carrier
      operatingCarrier
      transportId: flightNumber
      departureIata
      departureDateTime
      arrivalIata
      arrivalDateTime
      layoverTimeUntilNextSegmentInMinutes
      transportType
      vehicles
      transportationName
      isCabinMandatory
      durationInMinutes
    }
  }`);

export const ActivitySubIngredientFragment = gql(`
  fragment ActivitySubIngredient on TicketIngredientResponseQL {
    date
    suggested {
      code
      prices {
        totalPrice {
          ...Money
        }
      }
      activityTicketResults {
        quantity: quantityOfTickets
        rateId
        title
        ageRangeId
        description
        timeSlotId
        timeSlotStartTime
        supplierToken
        dateMainEvent
        ticketStartDate
        ticketEndDate
        isConfirmed
      }
    }
    alternativeSuggestion {
      code
      prices {
        totalPriceWithAlternative {
          ...Money
        }
      }
      activityTicketResults {
        quantity: quantityOfTickets
        rateId
        title
        ageRangeId
        description
        timeSlotId
        timeSlotStartTime
        supplierToken
        dateMainEvent
        ticketStartDate
        ticketEndDate
        isConfirmed
      }
    }
    type: __typename
  }`);

export const SEARCH_ITINERARIES_SUBSCRIPTION = gql(`
  subscription searchItineraries($recipe: RecipeQL!) {
    searchRecipeByItinerary(recipe: $recipe) {
      itineraryKey
      isSuggested
      totalPrice {
        ...Money
      }
      prices {
        totalPrice {
        ...Money
        }
        totalPriceWithAlternative {
          ...Money
        }
        grossPrice {
        ...Money
        }
        grossPriceWithAlternative {
          ...Money
        }
        pricePerPerson {
          ...Money
        }
        grossPricePerPerson {
          ...Money
        }
        pricePerPersonWithAlternative {
          ...Money
        }
        grossPriceWithAlternativePerPerson {
          ...Money
        }
        priceIsWithTaxesIncluded
      }
      result {
        ... on OwnStayResultQL {
          date
          numberOfNights
          subIngredientResults {
              ... on TicketIngredientResponseQL {
                ...ActivitySubIngredient
              }
              type: __typename
            }
        }
        ... on OwnTransportQL{
          date
          ownTransportType
        }
        ... on HotelIngredientResponseQL {
          lengthOfStay {
            numberOfNights
            nightsHaveBeenSuggested
          }
          date
          priceRange {
            from {
              ...Money
            }
            to {
              ...Money
            }
          }
          suggested {
            prices {
              grossPrice {
                ...Money
              }
              priceIsWithTaxesIncluded
            }
            addToBasketToken
            code: hotelCode
            totalPrice {
              ...Money
            }
            isRefundable
            rooms: roomStays {
              description
              basicDescription
              boardBasis
              pax {
                age
              }
              price {
                ...Money
              }
              cancellationPolicies {
                amount {
                  ...Money
                }
                fromDate
                fromDateIso
              }
              taxesAndFees {
                amount {
                  ...Money
                }
                type
                isIncludedInPrice
              }
            }
          }
          suggestions {
            addToBasketToken
            code: hotelCode
            prices {
              totalPrice {
                ...Money
              }
              deltaPrice {
                ...Money
              }
              grossPrice {
                ...Money
              }
              priceIsWithTaxesIncluded
            }
            isRefundable
            rooms: roomStays {
              description
              basicDescription
              boardBasis
              pax {
                age
              }
              price {
                ...Money
              }
              cancellationPolicies {
                amount {
                  ...Money
                }
                fromDate
                fromDateIso
              }
              taxesAndFees {
                amount {
                  ...Money
                }
                type
                isIncludedInPrice
              }
            }

          }
          alternativeSuggestion {
            addToBasketToken
            code: hotelCode
            totalPrice {
              ...Money
            }
            isRefundable
            rooms: roomStays {
              description
              basicDescription
              boardBasis
              roomCode
              pax {
                age
              }
              price {
                ...Money
              }
              cancellationPolicies {
                amount {
                  ...Money
                }
                fromDate
                fromDateIso
              }
              taxesAndFees {
                amount {
                  ...Money
                }
                type
                isIncludedInPrice
              }
            }
          }
          subIngredientResults {
            ... on TicketIngredientResponseQL {
              ...ActivitySubIngredient
            }
            type: __typename
          }
        }
        ... on TransportIngredientResponseQL {
          addToBasketToken
          date
          allCarriers
          durationRangeInMinutes {
            from
            to
          }
          totalPrice {
            ...Money
          }
          priceRange {
            from {
              amount
            }
            to {
              amount
            }
          }
          legs {
            ...Leg
          }
          legIndex
          alternativeSuggestion {
            addToBasketToken
            totalPrice: price {
              ...Money
            }
            legs {
              ...Leg
            }
            legIndex
          }
          suggested {
            rebookableOption {
              addToBasketToken
              rebookablePrice {
                ...Money
              }
              rebookableTotalPrice {
                ...Money
              }
            }
          }
        }
        type: __typename
      }
    }
  }
`);

export const SEARCH_ITINERARY_SUGGESTION_PRICES_SUBSCRIPTION = gql(`
  subscription searchItinerarySuggestionPrices($recipe: RecipeQL!) {
    searchRecipeByItinerary(recipe: $recipe) {
      itineraryKey
      isSuggested
      prices {
        totalPrice {
        ...Money
        }
        totalPriceWithAlternative {
          ...Money
        }
      }
      result {
        ... on OwnStayResultQL {
          date
        }
        ... on OwnTransportQL{
          date
        }
        ... on HotelIngredientResponseQL {
          date
        }
        ... on TransportIngredientResponseQL {
          date
        }
        type: __typename
      }
    }
  }
`);

export const SEARCH_DESTINATION_AVAILABILITY_SUBSCRIPTION = gql(`
  subscription searchDestinationAvailability(
    $request: CheapestStayByHotelRequestQL!
  ) {
    getCheapestStayByHotel(request: $request) {
      placeCode
      date
      prices {
        totalPrice {
          ...Money
        }
        grossPrice {
          ...Money
        }
        priceIsWithTaxesIncluded
      }
      rooms: roomStays {
        boardBasis
      }
    }
  }
`);

export const SEARCH_PRODUCT_AVAILABILITY_SUBSCRIPTION = gql(`
  subscription searchProductAvailability(
    $request: CheapestHotelStayRequestQL!
  ) {
    searchCheapestStay(request: $request) {
      date
      code: hotelCode
      totalPrice {
        ...Money
      }
      prices {
        totalPrice {
        ...Money
        }
        grossPrice {
        ...Money
        }
        priceIsWithTaxesIncluded
      }
      isRefundable
      addToBasketToken
      rooms: roomStays {
        notes {
          ...Note
        }
        description
        basicDescription
        boardBasis
        roomCode
        roomTypeId
        price {
          ...Money
        }
        cancellationPolicies {
          amount {
            ...Money
          }
          fromDate
          fromDateIso
        }
        taxesAndFees {
          amount {
            ...Money
          }
          type
          isIncludedInPrice
        }
        images {
          imageUrl
        }
        pax {
          age
        }
      }
    }
  }
`);

export const SEARCH_TICKET_RATES_QUERY = gql(`
  query searchTicketAvailability($request: TicketRequestQL!) {
    getTicketRates(request: $request) {
      hasFixedDates
      ratesByDay {
        date
        rates {
          rateId
          title
          description
          ageRanges {
            ageRangeId: id
            minAge
            maxAge
            minimumTicketsPerAgeRange
            price {
              ...Money
            }
            description
            availableTimeslotsPrices {
              availabletimeSlotId
              amount
              currency
              currencySymbol
              quantityAvailable
            }
            quantityAvailable
            supplierToken
          }
          requiredAgeRanges
          timeSlots {
            timeSlotId: id
            startTime
          }
          minimumAmountTickets
          maximumAmountTickets
          dateMainEvent
          ticketStartDate
          ticketEndDate
          estimatedDate
          isConfirmed
        }
      }
    }
  }
`);

export const GET_TICKET_BOOKING_TOKENS_QUERY = gql(`
  query getTicketBookingTokens($request: BookingTokenRequestQL!) {
    bookingTokens(request: $request)
  }
`);

export const GET_TRANSPORT_AVAILABILITY_QUERY = gql(`
  query getTransportAvailability($request: TransportListRequestQL!) {
    getTransportList(request: $request) {
      totalTransports
      transports {
        addToBasketToken
        totalPrice: price {
          ...Money
        }
        legs {
          ...Leg
        }
        legIndex
        isPromoted
        rebookableOption {
          addToBasketToken
          rebookablePrice {
            ...Money
          }
          rebookableTotalPrice {
            ...Money
          }
        }
      }
    }
  }
`);

export const GET_SUGGESTED_QUERY = gql(`
  query GetSuggested($request: GetSuggestedAvailabilityRequestQL!) {
    getSuggestedAvailability(request: $request) {
      results {
        code
        totalPrice {
          ...Money
        }
      }
    }
  }
`);
